/* eslint-disable no-param-reassign */
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { caseTransformingAxios } from "@shared/v2/caseTransformingAxios";
import axios from "axios";

export const initialState = {
  token: "",
  sites: [],
  siteUuid: null,
};

const api = (url, token) => {
  return axios.create({
    baseURL: url,
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
};

const syncSitesWithPageMaker = createAsyncThunk("areaPage/SyncWithSiteService", async () => {
  const response = await caseTransformingAxios.post("/website/page_maker/sync");
  return response;
});

const getAreaAutoComplete = createAsyncThunk("areaPage/getAreaAutocomplete", async (data) => {
  const response = await caseTransformingAxios.post("/website/autocomplete", data);
  return response;
});

const getSiteMlsIds = createAsyncThunk("areaPage/getSiteMlsIds", async (_, thunkApi) => {
  const response = await caseTransformingAxios.get(
    `/website/account_mlses?uuid=${thunkApi.getState().areaPage.organizationUuid}`,
  );
  return response;
});

// get token call
const getToken = createAsyncThunk("areaPage/getToken", async () => {
  const response = await fetch("/website/page_maker/token", { credentials: "same-origin" });
  const body = await response.json();

  return body;
});

// get sites call
const getSites = createAsyncThunk("areaPage/getSites", async (_, thunkApi) => {
  const websiteApi = api(thunkApi.getState().areaPage.apiUrl, thunkApi.getState().areaPage.token);
  const response = await websiteApi.get(`/sites`);
  return response.data;
});

// get pages call
const getPages = createAsyncThunk("areaPage/getPages", async (_, thunkApi) => {
  const websiteApi = api(thunkApi.getState().areaPage.apiUrl, thunkApi.getState().areaPage.token);
  const response = await websiteApi.get(
    `/sites/${thunkApi.getState().areaPage.siteUuid}/pages-with-settings`,
    { params: { categoryName: "Area" } },
  );
  return response.data;
});

// get page call
const getPage = createAsyncThunk("areaPage/getPage", async ({ uuid }, thunkApi) => {
  const websiteApi = api(thunkApi.getState().areaPage.apiUrl, thunkApi.getState().areaPage.token);
  const response = await websiteApi.get(`/sites/${thunkApi.getState().areaPage.siteUuid}/pages/${uuid}`);
  return response.data;
});

// update page call
const updatePage = createAsyncThunk("areaPage/updatePage", async (data, thunkApi) => {
  const websiteApi = api(thunkApi.getState().areaPage.apiUrl, thunkApi.getState().areaPage.token);

  const { siteUuid } = thunkApi.getState().areaPage;
  const { pageUuid } = thunkApi.getState().areaPage;

  const response = await websiteApi.patch(`/sites/${siteUuid}/pages/${pageUuid}`, data);
  return response.data;
});

// archive/unarchive page call
const archivePage = createAsyncThunk("areaPage/archivePage", async ({ uuid, isActive }, thunkApi) => {
  const websiteApi = api(thunkApi.getState().areaPage.apiUrl, thunkApi.getState().areaPage.token);
  const { siteUuid } = thunkApi.getState().areaPage;

  const response = await websiteApi.patch(`/sites/${siteUuid}/pages/${uuid}`, { isActive });
  return response.data;
});

// get page settings call
const getPageSettings = createAsyncThunk("areaPage/getPageSettings", async ({ uuid }, thunkApi) => {
  const websiteApi = api(thunkApi.getState().areaPage.apiUrl, thunkApi.getState().areaPage.token);
  const response = await websiteApi.get(
    `/sites/${thunkApi.getState().areaPage.siteUuid}/pages/${uuid}/settings`,
  );
  return response.data;
});

// add page call
const addPage = createAsyncThunk("areaPage/addPage", async ({ title, slug }, thunkApi) => {
  const state = thunkApi.getState();
  const newPageData = {
    originPageTemplateUuid: state.areaPage.areaPageTemplateUuid,
    name: title,
    path: `/${slug}`,
    slug,
    isActive: true,
  };

  const websiteApi = api(state.areaPage.apiUrl, state.areaPage.token);

  try {
    const response = await websiteApi.post(`/sites/${state.areaPage.siteUuid}/pages`, newPageData);
    return response.data;
  } catch (error) {
    return thunkApi.rejectWithValue({
      status: error.response?.status ?? null,
      data: error.response?.data ?? null,
      message: error.message,
    });
  }
});

// get page templates call
const getPageTemplates = createAsyncThunk("areaPage/getPageTemplates", async (_, thunkApi) => {
  const websiteApi = api(thunkApi.getState().areaPage.apiUrl, thunkApi.getState().areaPage.token);
  const response = await websiteApi.get(`/page-templates`, { params: { categoryName: "Area" } });
  return response.data;
});

// get page content components call
const getPageContentComponents = createAsyncThunk(
  "areaPage/getPageContentComponents",
  async (_, thunkApi) => {
    const websiteApi = api(thunkApi.getState().areaPage.apiUrl, thunkApi.getState().areaPage.token);
    const response = await websiteApi.get(
      `/sites/${thunkApi.getState().areaPage.siteUuid}/pages/${thunkApi.getState().areaPage.pageUuid}/content-components-with-bindings`,
    );
    return response.data;
  },
);

// get component call
const getComponent = createAsyncThunk("areaPage/getComponent", async ({ uuid }, thunkApi) => {
  const websiteApi = api(thunkApi.getState().areaPage.apiUrl, thunkApi.getState().areaPage.token);
  const response = await websiteApi.get(`/components/${uuid}`);
  return response.data;
});

// get components call
const getComponents = createAsyncThunk("areaPage/getComponents", async (_, thunkApi) => {
  const websiteApi = api(thunkApi.getState().areaPage.apiUrl, thunkApi.getState().areaPage.token);
  const response = await websiteApi.get(`/components`);
  return response.data;
});

// add page settings
const addPageSettings = createAsyncThunk("areaPage/addPageSettings", async (data, thunkApi) => {
  const websiteApi = api(thunkApi.getState().areaPage.apiUrl, thunkApi.getState().areaPage.token);
  const { siteUuid } = thunkApi.getState().areaPage;
  const { pageUuid } = thunkApi.getState().areaPage;
  const response = await websiteApi.get(`sites/${siteUuid}/pages/${pageUuid}/settings`, data);
  return response.data;
});

// update page settings
const updatePageSettings = createAsyncThunk("areaPage/updatePageSettings", async (data, thunkApi) => {
  const websiteApi = api(thunkApi.getState().areaPage.apiUrl, thunkApi.getState().areaPage.token);
  const { siteUuid } = thunkApi.getState().areaPage;
  const { pageUuid } = thunkApi.getState().areaPage;
  const response = await websiteApi.put(`sites/${siteUuid}/pages/${pageUuid}/settings`, data);
  return response.data;
});

// update content component
const updateContentComponent = createAsyncThunk(
  "areaPage/updateContentComponent",
  async ({ uuid, payload }, thunkApi) => {
    const websiteApi = api(thunkApi.getState().areaPage.apiUrl, thunkApi.getState().areaPage.token);
    const { siteUuid } = thunkApi.getState().areaPage;
    const { pageUuid } = thunkApi.getState().areaPage;

    const response = await websiteApi.patch(
      `sites/${siteUuid}/pages/${pageUuid}/content-components/${uuid}`,
      payload,
    );
    return response.data;
  },
);

// get content component dataadapter bindings
const getContentComponentBindings = createAsyncThunk(
  "areaPage/getContentComponentBindings",
  async (contentComponentUuid, thunkApi) => {
    const websiteApi = api(thunkApi.getState().areaPage.apiUrl, thunkApi.getState().areaPage.token);
    const { siteUuid } = thunkApi.getState().areaPage;
    const { pageUuid } = thunkApi.getState().areaPage;

    const response = await websiteApi.get(
      `/sites/${siteUuid}/pages/${pageUuid}/content-components/${contentComponentUuid}/bindings`,
    );
    return response.data;
  },
);

// update content component dataadapter bindings
const updateContentComponentBindings = createAsyncThunk(
  "areaPage/updateContentComponentBindings",
  async ({ contentComponentUuid, bindingUuid, payload }, thunkApi) => {
    const websiteApi = api(thunkApi.getState().areaPage.apiUrl, thunkApi.getState().areaPage.token);
    const { siteUuid } = thunkApi.getState().areaPage;
    const { pageUuid } = thunkApi.getState().areaPage;

    const response = await websiteApi.patch(
      `/sites/${siteUuid}/pages/${pageUuid}/content-components/${contentComponentUuid}/bindings/${bindingUuid}`,
      payload,
    );
    return response.data;
  },
);

// publish
const publish = createAsyncThunk("areaPage/publish", async (_, thunkApi) => {
  const websiteApi = api(thunkApi.getState().areaPage.apiUrl, thunkApi.getState().areaPage.token);
  const { siteUuid } = thunkApi.getState().areaPage;

  const response = await websiteApi.post(`/sites/${siteUuid}/publish`);
  return response.data;
});

const websiteSlice = createSlice({
  initialState,
  name: "areaPage",
  reducers: {
    setAreaSelection: (state, action) => {
      state.area = action.payload;
    },
  },
  extraReducers: (builder) => {
    // token builder
    builder
      .addCase(getToken.pending, (state) => {
        state.token = null;
      })
      .addCase(getToken.fulfilled, (state, action) => {
        state.token = action.payload.token;
        state.apiUrl = action.payload.api_url;
        state.tokenExpiresAt = action.payload.expires_at * 1000; // ms
      })
      .addCase(getToken.rejected, (state) => {
        state.tokenError = "token couldn't be created";
      });

    // get sites builder
    builder
      .addCase(getSites.pending, (state) => {
        state.sitesLoading = true;
        state.sites = null;
      })
      .addCase(getSites.fulfilled, (state, action) => {
        state.sites = action.payload.data.sites;
        state.siteUuid = 0;

        const mainSite = action.payload.data.sites.find((site) => site.isMain === true);
        if (mainSite) {
          state.siteUuid = mainSite.uuid;
          state.organizationUuid = mainSite.organizationUuid;
        }

        state.sitesLoading = false;
      })
      .addCase(getSites.rejected, (state, action) => {
        state.sitesError = action?.payload?.error || "error gettings sites";
        state.sitesLoading = false;
      });

    // get pages builder
    builder
      .addCase(getPages.pending, (state) => {
        state.pages = null;
        state.pagesLoading = true;
        state.pageUuid = null;
      })
      .addCase(getPages.fulfilled, (state, action) => {
        state.pages = action.payload.data.pages;
        state.pagesLoading = false;
      })
      .addCase(getPages.rejected, (state) => {
        state.pagesError = "Error getting pages";
        state.pagesLoading = false;
      });

    // get page builder
    builder
      .addCase(getPage.pending, (state) => {
        state.page = null;
        state.pageUuid = null;
        state.pageLoading = true;
      })
      .addCase(getPage.fulfilled, (state, action) => {
        state.page = action.payload.data;
        state.pageUuid = action.payload.data.uuid;
        state.pageLoading = false;
      })
      .addCase(getPage.rejected, (state) => {
        state.pageError = "Error getting page";
        state.pageLoading = false;
      });

    // get page settings builder
    builder
      .addCase(getPageSettings.pending, (state) => {
        state.pageSettings = null;
      })
      .addCase(getPageSettings.fulfilled, (state, action) => {
        state.pageSettings = action.payload.data;
      })
      .addCase(getPageSettings.rejected, (state) => {
        state.pageError = "Error getting page settings";
      });

    // add page builder
    builder
      .addCase(addPage.pending, (state) => {
        state.addPageLoading = true;
        state.addPageError = "";
      })
      .addCase(addPage.fulfilled, (state, action) => {
        state.addPageLoading = false;
        state.addPageError = "";
        state.pageUuid = action.payload.data.uuid;
      })
      .addCase(addPage.rejected, (state, action) => {
        state.addPageLoading = false;
        state.addPageError = action?.payload?.data?.error;
      });

    // get page templates builder
    builder
      .addCase(getPageTemplates.pending, () => {})
      .addCase(getPageTemplates.fulfilled, (state, action) => {
        // The index finds area pages by this template's category, so create from it too.
        const areaPageTemplate = action.payload.data.pageTemplates.find((template) => template.isActive);
        if (areaPageTemplate) {
          state.areaPageTemplateUuid = areaPageTemplate.uuid;
        }
      })
      .addCase(getPageTemplates.rejected, (state) => {
        state.pagesError = "Error getting template page categoriese";
      });

    // get page content components builder
    builder
      .addCase(getPageContentComponents.pending, (state) => {
        state.pageContentComponents = null;
      })
      .addCase(getPageContentComponents.fulfilled, (state, action) => {
        const pageContentComponents = [];
        state.pageContentComponents = null;

        action.payload.data.contentComponents.forEach((contentComponent) => {
          const uuid = contentComponent.componentUuid;

          const component = state.components.find((c) => c.uuid === uuid);

          const content = component.contentSchema;

          const fields = content.fields.reduce((acc, field) => {
            acc[field.key] = field.default;
            return acc;
          }, {});

          const merged = {
            uuid: contentComponent.uuid,
            name: component.name,
            isActive: contentComponent.isActive,
            bindings: contentComponent.bindings,
            ...fields,
            ...contentComponent.content,
          };

          pageContentComponents.push(merged);
        });

        state.pageContentComponents = pageContentComponents;
      })
      .addCase(getPageContentComponents.rejected, (state) => {
        state.pagesError = "Error getting page content components";
      });

    // get  component builder
    builder
      .addCase(getComponent.pending, () => {})
      .addCase(getComponent.fulfilled, () => {})
      .addCase(getComponent.rejected, (state) => {
        state.pagesError = "Error getting component";
      });

    // get components builder
    builder
      .addCase(getComponents.pending, () => {})
      .addCase(getComponents.fulfilled, (state, action) => {
        state.components = action.payload.data.items;
      })
      .addCase(getComponents.rejected, (state) => {
        state.pagesError = "Error getting component";
      });

    // get page templates builder
    builder
      .addCase(updateContentComponent.pending, () => {})
      .addCase(updateContentComponent.fulfilled, () => {})
      .addCase(updateContentComponent.rejected, (state) => {
        state.pagesError = "Error getting template page categoriese";
      });

    // update page
    builder
      .addCase(updatePage.pending, () => {})
      .addCase(updatePage.fulfilled, (state, action) => {
        state.page = action.payload.data;
      })

      .addCase(updatePage.rejected, () => {});

    // archive/unarchive page
    builder
      .addCase(archivePage.pending, () => {})
      .addCase(archivePage.fulfilled, () => {})
      .addCase(archivePage.rejected, (state) => {
        state.pagesError = "Error archiving page";
      });

    // update page settings
    builder
      .addCase(updatePageSettings.pending, () => {})
      .addCase(updatePageSettings.fulfilled, (state, action) => {
        state.pageSettings = action.payload.data;
      })

      .addCase(updatePageSettings.rejected, () => {});

    builder
      .addCase(getSiteMlsIds.pending, (state) => {
        state.mlsIds = [];
      })
      .addCase(getSiteMlsIds.fulfilled, (state, action) => {
        state.mlsIds = action.payload.data;
      })
      .addCase(getSiteMlsIds.rejected, () => {});
  },
});

export {
  syncSitesWithPageMaker,
  getSites,
  getToken,
  getPages,
  getPage,
  archivePage,
  getPageSettings,
  addPage,
  getPageContentComponents,
  getComponent,
  getComponents,
  getPageTemplates,
  addPageSettings,
  updateContentComponent,
  updatePageSettings,
  updatePage,
  getContentComponentBindings,
  updateContentComponentBindings,
  publish,
  getAreaAutoComplete,
  getSiteMlsIds,
};

export const { setAreaSelection } = websiteSlice.actions;

export default websiteSlice.reducer;
